import { TFunction } from 'i18next';

import { HIGHLIGHT, HighLightConfigType } from '~/modules/highlight/constants';
import { REACTION } from '~/modules/reactions/types';
import { ExtendedAiResearcherItem } from '~/modules/tools/views/AiResearcher/types';
import analytics from '~/services/analytics';

export const TIMER_VALUES = {
  research_report: 2 * 60 * 1000,
  detailed_report: 5 * 60 * 1000,
};

export const getLogTranslations = (
  t: TFunction,
): Record<string, () => string> => ({
  starting_research: () =>
    t('ai_researcher:log.staring_research', {
      defaultValue: '🔍 Starting the research',
    }),
  agent_generated: () =>
    t('ai_researcher:log.agent_generated', {
      defaultValue: '🤖 AI Assistant Agent',
    }),
  planning_research: () =>
    t('ai_researcher:log.planning_research', {
      defaultValue: '🌐 Browsing the web to learn more about the task',
    }),
  running_subquery_research: () =>
    t('ai_researcher:log.running_subquery_research', {
      defaultValue: '🔍 Running research...',
    }),
  added_source_url: () =>
    t('ai_researcher:log.added_source_url', {
      defaultValue: '✅ Added source urls to research',
    }),
  researching: () =>
    t('ai_researcher:log.researching', {
      defaultValue:
        '🤔 Researching for relevant information across multiple sources...',
    }),
  scraping_urls: () =>
    t('ai_researcher:log.scraping_urls', {
      defaultValue: '🌐 Scraping content from URLs...',
    }),
  scraping_content: () =>
    t('ai_researcher:log.scraping_content', {
      defaultValue: '📄 Scraping content...',
    }),
  scraping_images: () =>
    t('ai_researcher:log.scraping_images', {
      defaultValue: '🖼️ Scraping images...',
    }),
  scraping_complete: () =>
    t('ai_researcher:log.scraping_complete', {
      defaultValue: '🌐 Scraping complete',
    }),
  fetching_query_content: () =>
    t('ai_researcher:log.fetching_query_content', {
      defaultValue: '📚 Getting relevant content based on query...',
    }),
  writing_report: () =>
    t('ai_researcher:log.fetching_query_content', {
      defaultValue: '✍️ Writing report for...',
    }),
});

export const DEFAULT_MAX_REQUESTS_RESEARCH_DAILY_LIMIT = 5;

export const EXAMPLE_RESPONSE: ExtendedAiResearcherItem = {
  id: 'example',
  task: 'Space Technologies Market Dynamics and Trends for 2025',
  output:
    "# Space Technologies Market Dynamics and Trends for 2025\n\n## Introduction\n\nThe space technology market is poised for significant growth and transformation in 2025, driven by advancements in satellite technology, artificial intelligence (AI), multi-orbit satellite systems, and increasing global collaboration. With the global space economy valued at USD 418 billion in 2024 and projected to reach USD 788.7 billion by 2034, the market is expanding at a compound annual growth rate (CAGR) of 6.7% ([Global](https://www.gminsights.com/industry-analysis/space-economy-market)). This report delves into the dynamics and trends shaping the space technology market in 2025, focusing on key innovations, market drivers, challenges, and opportunities.\n\n---\n\n## Market Dynamics\n\n### Growth Drivers\n\n1. **Increased Satellite Deployments**\n   The number of satellites in orbit has grown exponentially in recent years. In 2024, 2,781 commercial satellites were deployed, marking a 20% increase from the previous year. As of 2025, the total number of satellites in orbit stands at 9,691, reflecting a staggering 361% increase over the past five years ([Satellite World Today](https://satelliteworldtoday.com/trends-transforming-the-space-industry-in-2025/)). This growth is fueled by advancements in satellite miniaturization, cost reductions in manufacturing, and the demand for satellite-based services such as Earth observation, communication, and navigation.\n\n2. **Multi-Orbit Satellite Systems**\n   Multi-orbit satellite systems, which integrate low Earth orbit (LEO), medium Earth orbit (MEO), and geostationary orbit (GEO) satellites, are transforming the industry. These systems enable seamless connectivity and improved performance for enterprises, governments, and telecommunications providers. Multi-orbit solutions are particularly valuable for applications such as IoT, data relays, and AI-integrated services, broadening the scope of satellite usage ([SES](https://www.ses.com/blog/ses-2025-predictions-multi-orbit-satellites-will-drive-innovation-enterprises-governments-and)).\n\n3. **Artificial Intelligence and Machine Learning**\n   AI and machine learning (ML) are being increasingly integrated into space systems to enhance data analytics, satellite operations, and mission planning. AI/ML applications include real-time data processing, predictive maintenance of satellites, and autonomous navigation in space ([Lockheed Martin](https://www.lockheedmartin.com/en-us/news/features/2024/space-technology-trends-2025.html)).\n\n4. **Commercial Space Activities**\n   The commercialization of space is accelerating, with private companies like SpaceX, Blue Origin, and others driving innovation. SpaceX's Starship program, for instance, is expected to achieve significant milestones in 2025, including orbital flights and on-orbit fuel transfer demonstrations. Additionally, Blue Origin's New Glenn rocket is slated for its maiden launch in early 2025 ([SpaceFund](https://spacefund.com/2025-expectations/)).\n\n5. **Government Investments and Policy Support**\n   Governments worldwide are increasing their investments in space exploration and technology. NASA's adoption of a commercial space model, including the Commercial LEO Destination program and the Lunar Terrain Vehicle Services rover program, exemplifies the growing collaboration between public and private sectors ([SpaceQ](https://spaceq.ca/what-to-expect-from-the-space-industry-in-2025/)).\n\n---\n\n### Challenges\n\n1. **Space Debris and Sustainability**\n   The rapid increase in satellite deployments has led to a growing concern over space debris. Effective debris control measures and sustainable practices are becoming critical to ensure the long-term viability of space operations ([Satellite World Today](https://satelliteworldtoday.com/trends-transforming-the-space-industry-in-2025/)).\n\n2. **Crowded Competitive Landscape**\n   The space industry is becoming increasingly competitive, with new entrants and startups challenging established players. This intensifies the need for innovation and differentiation to maintain market share ([SpaceQ](https://spaceq.ca/what-to-expect-from-the-space-industry-in-2025/)).\n\n3. **Regulatory and Policy Shifts**\n   Shifting policy priorities and regulatory changes can create uncertainties for space companies. Navigating these changes while maintaining compliance and fostering innovation is a key challenge for the industry ([SpaceQ](https://spaceq.ca/what-to-expect-from-the-space-industry-in-2025/)).\n\n---\n\n## Key Trends in 2025\n\n### 1. **Proliferated Satellite Constellations**\nProliferated satellite constellations, such as SpaceX's Starlink and Amazon's Project Kuiper, are revolutionizing global connectivity. These constellations aim to provide high-speed internet access to underserved and remote areas, bridging the digital divide. The deployment of mega-constellations is expected to continue in 2025, further enhancing global connectivity ([Lockheed Martin](https://www.lockheedmartin.com/en-us/news/features/2024/space-technology-trends-2025.html)).\n\n### 2. **Earth Observation and Data Analytics**\nThe Earth observation (EO) market is experiencing rapid growth, with investments reaching USD 1.7 billion in 2024. Satellite data analytics is becoming increasingly sophisticated, enabling applications in agriculture, disaster management, climate monitoring, and urban planning. The global satellite data services market is projected to grow at a CAGR of 16% from 2024 to 2030 ([StartUs Insights](https://www.startus-insights.com/innovators-guide/space-technology-trends/)).\n\n### 3. **Space-Based Quantum Communication**\nAdvancements in quantum communication technology are paving the way for secure and efficient data transmission in space. This technology has significant implications for cybersecurity, military communications, and scientific research ([Lockheed Martin](https://www.lockheedmartin.com/en-us/news/features/2024/space-technology-trends-2025.html)).\n\n### 4. **Lunar Exploration**\nLunar exploration is gaining momentum, with NASA and private companies focusing on missions to the Moon. NASA's Artemis program and commercial lunar lander initiatives are expected to achieve significant milestones in 2025. These efforts aim to establish a sustainable human presence on the Moon and serve as a stepping stone for future Mars missions ([SpaceQ](https://spaceq.ca/what-to-expect-from-the-space-industry-in-2025/)).\n\n### 5. **Advanced Manufacturing and 3D Printing**\nAdvanced manufacturing techniques, including 3D printing, are being utilized to produce spacecraft components more efficiently and cost-effectively. This trend is expected to accelerate in 2025, enabling rapid prototyping and reducing production timelines ([Lockheed Martin](https://www.lockheedmartin.com/en-us/news/features/2024/space-technology-trends-2025.html)).\n\n---\n\n## Market Segmentation\n\n### By Technology Type\n- **Launch Vehicles**: Innovations in reusable rockets and cost-effective launch solutions are driving growth in this segment.\n- **Satellites**: Miniaturized satellites and advanced payloads are expanding the capabilities of satellite technology.\n- **Space Robotics**: Robotics are being used for on-orbit servicing, assembly, and exploration missions.\n- **Propulsion Systems**: Nuclear propulsion and electric propulsion systems are gaining traction for deep-space missions ([MetaTech Insights](https://www.metatechinsights.com/industry-insights/space-technology-market-1502)).\n\n### By Application\n- **Satellite Communications**: Enabling global connectivity and IoT applications.\n- **Earth Observation**: Supporting environmental monitoring, disaster management, and resource management.\n- **Space Exploration**: Advancing human and robotic exploration of the Moon, Mars, and beyond ([The Business Research Company](https://www.thebusinessresearchcompany.com/report/space-technology-spacetech-global-market-report)).\n\n---\n\n## Future Outlook\n\nThe space technology market is expected to witness sustained growth and innovation in the coming years. Key players such as SpaceX, Lockheed Martin, Airbus, and Blue Origin are driving advancements in satellite technology, launch systems, and space exploration. Additionally, the increasing involvement of startups and new entrants is fostering a dynamic and competitive landscape.\n\nThe focus on sustainability, international collaboration, and policy support will be critical in addressing challenges and unlocking new opportunities in the space sector. With continued investments and technological breakthroughs, 2025 is set to be a transformative year for the space technology market.\n\n---\n\n## References\n\n1. Satellite World Today. (2025, January 21). Trends Transforming the Space Industry in 2025. [https://satelliteworldtoday.com/trends-transforming-the-space-industry-in-2025/](https://satelliteworldtoday.com/trends-transforming-the-space-industry-in-2025/)\n2. SES. (2025). SES 2025 Predictions: Multi-Orbit Satellites Will Drive Innovation. [https://www.ses.com/blog/ses-2025-predictions-multi-orbit-satellites-will-drive-innovation-enterprises-governments-and](https://www.ses.com/blog/ses-2025-predictions-multi-orbit-satellites-will-drive-innovation-enterprises-governments-and)\n3. Lockheed Martin. (2024, December 3). Space Technology Trends 2025. [https://www.lockheedmartin.com/en-us/news/features/2024/space-technology-trends-2025.html](https://www.lockheedmartin.com/en-us/news/features/2024/space-technology-trends-2025.html)\n4. SpaceFund. (2025). 2025 Expectations. [https://spacefund.com/2025-expectations/](https://spacefund.com/2025-expectations/)\n5. SpaceQ Media Inc. (2024, December 20). What to Expect from the Space Industry in 2025. [https://spaceq.ca/what-to-expect-from-the-space-industry-in-2025/](https://spaceq.ca/what-to-expect-from-the-space-industry-in-2025/)\n6. Global Market Insights. (2024). Space Economy Market Size, Share \u0026 Trends Analysis, 2025-2034. [https://www.gminsights.com/industry-analysis/space-economy-market](https://www.gminsights.com/industry-analysis/space-economy-market)\n7. MetaTech Insights. (2024). Space Technology Market Size, Share, and Growth 2025-2035. [https://www.metatechinsights.com/industry-insights/space-technology-market-1502](https://www.metatechinsights.com/industry-insights/space-technology-market-1502)\n8. The Business Research Company. (2025). Space Technology (SpaceTech) Market Report 2025. [https://www.thebusinessresearchcompany.com/report/space-technology-spacetech-global-market-report](https://www.thebusinessresearchcompany.com/report/space-technology-spacetech-global-market-report)",
  reaction: REACTION.NONE,
  pdf_link:
    'https://dbmuuy3hm30xy.cloudfront.net/ai_researches/f104820b-6cc4-437c-b296-f1eb0d5047a3.pdf',
  is_in_progress: false,
  demo_mode: true,
  ai_model: '',
};

export const IsShowedResearcherTutorial = 'is_showed_researcher_tutorial';

export const AiResearcherTitle = 'AI Researcher';

export const getAiResearcherHighlightConfig = (
  t: TFunction,
): Record<
  | HIGHLIGHT.AI_RESEARCHER_TOPIC
  | HIGHLIGHT.AI_RESEARCHER_TONE
  | HIGHLIGHT.AI_RESEARCHER_TYPE
  | HIGHLIGHT.AI_RESEARCHER_SOURCES
  | HIGHLIGHT.AI_RESEARCHER_CITATIONS
  | HIGHLIGHT.AI_RESEARCHER_REPORT_FILE,
  HighLightConfigType
> => {
  return {
    [HIGHLIGHT.AI_RESEARCHER_TOPIC]: {
      title: t('highlight:researcher.topic.title', 'Research Topic'),
      text: t(
        'highlight:researcher.topic.text',
        'Specify what you want to do research about',
      ),
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiResearcherTitle,
          tip_number: 1,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiResearcherTitle,
          tip_number: 1,
        });
      },
    },
    [HIGHLIGHT.AI_RESEARCHER_TONE]: {
      title: t('highlight:researcher.tone.title', 'Tone Of The Report'),
      text: t(
        'highlight:researcher.tone.text',
        'Choose the tone of the report to adjust the narrative, such as “Optimistic” or “Pessimistic”',
      ),
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiResearcherTitle,
          tip_number: 2,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiResearcherTitle,
          tip_number: 2,
        });
      },
    },
    [HIGHLIGHT.AI_RESEARCHER_TYPE]: {
      title: t('highlight:researcher.type.title', 'Report Type'),
      text: t(
        'highlight:researcher.type.text',
        'Choose the length of the research report. Long reports take longer to generate but also provide more details.',
      ),
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiResearcherTitle,
          tip_number: 3,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiResearcherTitle,
          tip_number: 3,
        });
      },
    },
    [HIGHLIGHT.AI_RESEARCHER_SOURCES]: {
      title: t('highlight:researcher.sources.title', 'Sources'),
      text: t(
        'highlight:researcher.sources.text',
        'A link to a source backs each piece of information in the report',
      ),
      placement: 'top-start',
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiResearcherTitle,
          tip_number: 4,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiResearcherTitle,
          tip_number: 4,
        });
      },
    },
    [HIGHLIGHT.AI_RESEARCHER_CITATIONS]: {
      title: t(
        'highlight:researcher.citations.title',
        'Citations (References)',
      ),
      text: t(
        'highlight:researcher.citations.text',
        'All sources are listed at the end of each report in APA format',
      ),
      buttonText: t('general:next', 'Next'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiResearcherTitle,
          tip_number: 5,
        });
      },
      onSubmitCallback: (): void => {
        analytics.trackEvent('tutorial - click next', {
          tool: AiResearcherTitle,
          tip_number: 5,
        });
      },
    },
    [HIGHLIGHT.AI_RESEARCHER_REPORT_FILE]: {
      title: t('highlight:researcher.report_file.title', 'Report File'),
      text: t(
        'highlight:researcher.report_file.text',
        'Download the formatted report document in the preferred file type',
      ),
      buttonText: t('general:finish', 'Finish'),
      onShowCallBack: (): void => {
        analytics.trackEvent('tutorial - show tip', {
          tool: AiResearcherTitle,
          tip_number: 5,
        });
      },
      onSubmitCallback: (updateUserExtraFields): void => {
        updateUserExtraFields({
          highlights: { [IsShowedResearcherTutorial]: true },
        });
        analytics.trackEvent('tutorial - finished', {
          tool: AiResearcherTitle,
        });
      },
    },
  };
};
